import { createContext, useContext, ReactNode, useState } from 'react'
import { Column } from '@tanstack/react-table'
import { useLogisticInventory } from '../hooks/useLogisticInventory'
import { InventoryItem } from '../interfaces/inventory'

interface TableContextType {
  currentPage: number
  setCurrentPage: (_page: number) => void
  pageSize: number
  setPageSize: (_size: number) => void
  searchQuery: string
  setSearchQuery: (_query: string) => void
  inventoryData: InventoryItem[]
  isLoading: boolean
  skeletonRowCount: number
  isFetching: boolean
  isError: boolean
  refetch: () => void
  totalItems: number
  totalPages: number
  filters: Record<string, unknown>
  setFilters: (_filters: Record<string, unknown>) => void
  columns: Column<InventoryItem, unknown>[]
  setColumns: (_columns: Column<InventoryItem, unknown>[]) => void
}

const TableContext = createContext<TableContextType | undefined>(undefined)

interface TableContextProviderProps {
  children: ReactNode
}

export function TableContextProvider({ children }: TableContextProviderProps) {
  const {
    inventoryData,
    isLoading,
    isFetching,
    isError,
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    searchQuery,
    setSearchQuery,
    refetch,
    totalItems,
    totalPages,
    filters,
    setFilters,
  } = useLogisticInventory()

  const [columns, setColumns] = useState<Column<InventoryItem, unknown>[]>([])

  const skeletonRowCount = pageSize

  return (
    <TableContext.Provider
      value={{
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,
        searchQuery,
        setSearchQuery,
        inventoryData,
        isLoading,
        skeletonRowCount,
        isFetching,
        isError,
        refetch,
        totalItems,
        totalPages,
        filters,
        setFilters,
        columns,
        setColumns,
      }}
    >
      {children}
    </TableContext.Provider>
  )
}

export function useTableContext() {
  const context = useContext(TableContext)
  if (context === undefined) {
    throw new Error(
      'useTableContext must be used within a TableContextProvider'
    )
  }
  return context
}
